import {css, html} from 'lit';
import {customElement} from 'lit/decorators.js';
import {
  combineLatest,
  filter,
  fromEvent,
  interval,
  map,
  of,
  startWith,
  tap,
} from 'rxjs';

import Decimal from './break_infinity';
import {Panel} from './panel';
import {ri} from './runner-idle';
import {formatEng} from './utils';

@customElement('ri-map')
export class MapPanel extends Panel {
  // #region Properties (9)

  #isBig = false;

  public static override styles = [
    ...Panel.styles,
    css`
      #map-canvas {
        height: calc(100% - 24px);
      }
      #map-extra-info {
        position: absolute;
        bottom: 6ch;
        left: 0%;
        mix-blend-mode: difference;
        color: white;
      }
    `,
  ];

  canvas!: HTMLElement;
  public override helpContent = `This is the <strong>Map</strong> panel!
  <br>Click anywhere to set your target position to that point.
  <br>Whenever you move, you will move towards your target position.
  <br>Areas outside your field of view are black;
  <br>gain Perception to see farther (zoom out farther).
  `;
  public override id = 'map';
  public initialSize: [number, number] = [600, 200];
  public minZoom = 0;
  public override name = 'Map';
  public zoom: Decimal = new Decimal(0);

  // #endregion Properties (9)

  // #region Public Methods (3)

  public override render() {
    return super.render(html`
      <div
        id="map-canvas"
        @wheel=${(event: WheelEvent) => {
          const zoomRangeEl = this.shadowRoot?.getElementById('zoom-range');
          if (zoomRangeEl instanceof HTMLInputElement) {
            zoomRangeEl.value = String(
              Number(zoomRangeEl.value) + (event.deltaY > 0 ? -0.1 : 0.1)
            );
          }
        }}
      ></div>
      <div id="map-extra-info" class="flex-vertical flex-stretch">
        <label for="zoom-range">
          Zoom:
          <input
            id="zoom-range"
            type="range"
            value="1.5"
            min=${Math.round(this.minZoom * 10) / 10}
            max="3.5"
            step="0.1"
        /></label>
      </div>
    `);
  }

  public override setup() {
    super.setup();
    this.settings = ri.settings.map;
    this.canvas = this.shadowRoot!.getElementById('map-canvas') as HTMLElement;
    fromEvent(this.canvas, 'dblclick').subscribe(() => {
      this.onFullscreenToggle();
      this.toggleSize();
    });
    this.subscribe(
      'minZoom',
      ri.stats.perception.value$.pipe(map((value) => 0 - value.log10()))
    );
    this.subscribe(
      'zoom',
      fromEvent(this.shadowRoot!.getElementById('zoom-range')!, 'input').pipe(
        map(
          (event) =>
            new Decimal((event.target as HTMLInputElement).valueAsNumber)
        )
      )
    );
    // Set subtitle to current landmark breadcrumbs
    this.subscribe(
      'subtitle',
      combineLatest(
        Object.values(ri.landmarks)
          .sort((a, b) => {
            return a.z.minus(b.z).toNumber();
          })
          .map((landmark) =>
            combineLatest([of(landmark.displayName), landmark.containsPlayer$])
          )
      ).pipe(
        map((landmarks) =>
          landmarks
            .filter(([, landmark]) => landmark)
            .map(([name]) => name)
            .join(' > ')
        )
      )
    );
    this.subscribe(
      'initialSize',
      interval(1000).pipe(
        startWith(),
        filter(() => !this.#isBig),
        map(
          () =>
            [this.canvas.clientWidth, this.canvas.clientHeight - 3] as [
              number,
              number
            ]
        ),
        tap(() => {
          ri.resizeMapCanvas(this.initialSize);
          this.requestUpdate();
        })
      )
    );
  }

  public toggleSize() {
    if (this.#isBig) {
      ri.resizeMapCanvas(this.initialSize);
    } else {
      ri.resizeMapCanvas([
        this.canvas.parentElement?.clientWidth || this.initialSize[0],
        (this.parentElement?.clientHeight || this.initialSize[1]) - 100,
      ]);
    }
    this.#isBig = !this.#isBig;
  }

  // #endregion Public Methods (3)
}
